import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Clock, XCircle, Send, ArrowRight } from "lucide-react";
import { API } from "../App";
import { logger } from "../lib/logger";

const QuoteResponsePage = () => {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const action = searchParams.get("action");
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const response = await axios.get(`${API}/quotes/${quoteId}`);
        setQuote(response.data);
      } catch (error) {
        logger.error("Failed to fetch quote:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [quoteId]);

  useEffect(() => {
    // Auto-submit for "ready" action - redirect to quote
    if (action === "ready" && quote && !submitted) {
      recordResponse("ready");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, quote, submitted]);

  const recordResponse = async (responseType, msg = null) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes/${quoteId}/followup-response`, {
        quote_id: quoteId,
        response_type: responseType,
        message: msg
      });
      setSubmitted(true);
      
      // If ready, redirect to quote after brief delay
      if (responseType === "ready") {
        setTimeout(() => {
          navigate(`/quote/${quoteId}`);
        }, 1500);
      }
    } catch (error) {
      logger.error("Failed to record response:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleThinkingSubmit = () => {
    recordResponse("thinking", message);
  };

  const handleNotNowConfirm = () => {
    recordResponse("not_now");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00341d]"></div>
      </div>
    );
  }

  // Ready to go - redirect to quote
  if (action === "ready") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#00341d] mb-4">Great to hear!</h1>
            <p className="text-zinc-600 mb-6">
              Taking you to your quote now so you can review and sign...
            </p>
            <div className="flex items-center justify-center gap-2 text-[#00341d]">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#00341d]"></div>
              <span>Redirecting...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Still thinking
  if (action === "thinking") {
    if (submitted) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
          <Card className="max-w-lg w-full shadow-xl border-0">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#00341d] mb-4">Message Sent!</h1>
              <p className="text-zinc-600 mb-6">
                Thanks for letting us know. We'll be in touch soon.
              </p>
              <Button
                onClick={() => navigate(`/quote/${quoteId}`)}
                className="bg-[#00341d] hover:bg-[#002816]"
              >
                View Your Quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full shadow-xl border-0">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#00341d] mb-4 text-center">Take your time.</h1>
            <p className="text-zinc-600 mb-6 text-center">
              We understand it's a big decision for your property. I'll keep your quote on file.
            </p>
            <p className="text-zinc-700 mb-4 text-center font-medium">
              Is there any specific information or a different clearing option I can provide to help you decide?
            </p>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here... (optional)"
              className="min-h-[120px] mb-4"
            />
            <div className="flex gap-3">
              <Button
                onClick={handleThinkingSubmit}
                disabled={submitting}
                className="flex-1 bg-[#d97706] hover:bg-[#b45309]"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Sending...
                  </span>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
              <Button
                onClick={() => navigate(`/quote/${quoteId}`)}
                variant="outline"
              >
                View Quote
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not right now
  if (action === "not_now") {
    if (submitted) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-zinc-100 to-white flex items-center justify-center p-4">
          <Card className="max-w-lg w-full shadow-xl border-0">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-zinc-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-zinc-600" />
              </div>
              <h1 className="text-2xl font-bold text-zinc-800 mb-4">Got it!</h1>
              <p className="text-zinc-600 mb-6">
                We've noted your preference. Feel free to reach out whenever you're ready.
              </p>
              <p className="text-sm text-zinc-500">
                Your quote will remain accessible if you change your mind.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-100 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-zinc-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-zinc-500" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-800 mb-4">Understood.</h1>
            <p className="text-zinc-600 mb-6">
              We'll move this to the "inactive" list for now so we don't clog up your inbox. 
              We'll be here whenever you're ready to get that land cleared.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleNotNowConfirm}
                disabled={submitting}
                variant="outline"
                className="border-zinc-300"
              >
                {submitting ? "Confirming..." : "Confirm"}
              </Button>
              <Button
                onClick={() => navigate(`/quote/${quoteId}`)}
                className="bg-[#00341d] hover:bg-[#002816]"
              >
                Actually, let me look at the quote
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid action
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardContent className="p-8 text-center">
          <h1 className="text-xl font-bold text-zinc-800 mb-4">Invalid Link</h1>
          <p className="text-zinc-600 mb-6">
            This link appears to be invalid or has expired.
          </p>
          <Button
            onClick={() => navigate(`/quote/${quoteId}`)}
            className="bg-[#00341d] hover:bg-[#002816]"
          >
            View Quote
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuoteResponsePage;
